import React, { useCallback, useState, useMemo, useEffect } from 'react';
import BudgetToActualComponents from '../../components/budgettoactual/BudgetToActualComponents';
import BudgetToActualDirectComponents from '../../components/budgettoactual/BudgetToActualDirectComponents';
import BudgetToActualPublicComponents from '../../components/budgettoactual/BudgetToActualPublicComponents';
import BudgetToActualSbdComponents from '../../components/budgettoactual/BudgetToActualSbdComponents';
import {
  Box, Typography,
  Button,  MenuItem, Select, FormControl
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { getDeptList, getBudget, getBudgetRange } from '../../lib/api/establish';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';

const useStyles = makeStyles({
  container: { padding: '16px', backgroundColor: '#ffffff', marginBottom: '-35px' },
  headerTitle: { fontWeight: 600, color: '#000000', marginBottom: '20px' },
  searchArea: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' },
});

const BudgetToActualContainer = () => {
  const classes = useStyles();
  const { user } = useSelector(({ auth }) => ({
    user: auth.user,
  }));

  const [isDirectOpen, setDirectIsOpen] = useState(false);
  const [isPublicOpen, setPublicIsOpen] = useState(false);
  const [isSbdOpen, setSbdIsOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const currentYear = new Date().getFullYear();
  const [searchYear, setSearchYear] = useState(currentYear);
  const [startMonth, setStartMonth] = useState('01');
  const currentMonth = (new Date().getMonth() + 1).toString().padStart(2, '0');
  const [endMonth, setEndMonth] = useState(currentMonth);
  const [budgetToActual, setBudgetToActual] = useState([]); 
  const [budgetToActualDirect, setBudgetToActualDirect] = useState([]); 
  const [budgetToActualPublic, setBudgetToActualPublic] = useState([]); 
  const [budgetToActualSbd, setBudgetToActualSbd] = useState([]); 

  const [loading, setLoading] = useState(false);

  const yearOptions = useMemo(() => {
    return [currentYear, currentYear + 1];
  }, [currentYear]);

  const monthOptions = Array.from({ length: 12 }, (_, i) => 
    (i + 1).toString().padStart(2, '0')
  );

  const [appliedRange, setAppliedRange] = useState({
    year: '',
    start: '',
    end: ''
  });

  const fetchInitialData = useCallback(async (year, start, end) => {
    const targetYear = year || searchYear;
    const targetStart = start || startMonth;
    const targetEnd = end || endMonth;

    if (!targetYear) return;
    setLoading(true);

    try {
      const [deptRes, budgetRes, budgetRangeRes] = await Promise.allSettled([
        getDeptList(),
        getBudget(targetYear),
        getBudgetRange(targetYear, targetStart, targetEnd)
      ]);

      const allDepts = deptRes.status === 'fulfilled' ? (deptRes.value.data?.list || []) : [];

      const depts = allDepts.filter(dept => {
        if (user?.adminRole === 'Y') return true;

        const isMyDept = dept.dptUuid === user?.dptUuid;
        const isMeLeader = user?.usrUuid === user?.dptInf?.leaderUuid;

        return isMyDept && isMeLeader;
      });

      if (depts.length === 0) {
        alert("해당 메뉴에 대한 조회 권한이 없습니다. (관리자 또는 부서장 전용)");

        setBudgetToActual([]);
        setBudgetToActualDirect([]);
        setBudgetToActualPublic([]);
        setBudgetToActualSbd([]);
        
        setLoading(false);
        return;
      }

      const budgetToActualList = budgetRes.status === 'fulfilled' ? (budgetRes.value.data?.list || []) : [];
      const bugetRangeData = budgetRangeRes.status === 'fulfilled' ? (budgetRangeRes.value.data?.list || []) : [];

      const budgetSummaryMap = bugetRangeData.reduce((acc, row) => {
        const key = row.dptUuid;
        if (!acc[key]) acc[key] = { payCorpCommon: 0, payDrExp: 0, paySbuCmn: 0 };
        acc[key].payCorpCommon += (row.payCorpCommon || 0);
        acc[key].payDrExp += (row.payDrExp || 0);
        acc[key].paySbuCmn += (row.paySbuCmn || 0);
        return acc;
      }, {});

      const budgetDeptMap = bugetRangeData.reduce((acc, row) => {
        const key = row.dptUuid;
        if (!acc[key]) acc[key] = [];
        acc[key].push(row);
        return acc;
      }, {});

      const processedData = depts.map(dept => {
        const rowData = budgetToActualList.find(b => b.dptUuid === dept.dptUuid);
        const summary = budgetSummaryMap[dept.dptUuid] || { payCorpCommon: 0, payDrExp: 0, paySbuCmn: 0 };
        const monthlyList = budgetDeptMap[dept.dptUuid] || [];
        const sortedMonthly = [...monthlyList].sort((a, b) => (a.dtMonth || 0) - (b.dtMonth || 0));

        return {
          id: dept.dptUuid,
          dept: dept.dptNm,
          totalBudgetAmt: rowData?.totalBudgetAmt || 0,
          directAmt: rowData?.directAmt || 0,
          commonBudgetAmt: rowData?.commonBudgetAmt || 0,
          strategyBudgetAmt: rowData?.strategyBudgetAmt || 0,
          summary,
          sortedMonthly
        };
      });

      // 🛠️ [버그 완치]: 리스트의 '길이(개수)'가 아닌 데이터 내부의 '최대 월 숫자'를 찾아 방어벽을 세웁니다.
      const monthlyDataList = processedData[0]?.sortedMonthly || [];
      const maxRegisteredMonth = monthlyDataList.length > 0 
        ? Math.max(...monthlyDataList.map(m => parseInt(m.dtMonth || 0, 10))) 
        : 0;
        
      const selectedEndNum = parseInt(targetEnd, 10);
      let finalEndStr = targetEnd;

      // 데이터가 존재하고, 유저가 선택한 종료월이 데이터 내에 존재하는 실제 최대 월 한도를 넘어섰을 때만 보정
      if (maxRegisteredMonth > 0 && selectedEndNum > maxRegisteredMonth) {
        finalEndStr = String(maxRegisteredMonth).padStart(2, '0');
        setEndMonth(finalEndStr);
      }

      setAppliedRange({
        year: targetYear,
        start: targetStart,
        end: finalEndStr
      });

      // 하단 데이터 세팅 영역 유지
      const isMyDeptFlag = user?.adminRole === 'Y';
      
      setBudgetToActual(processedData.map(d => ({
        isMyDept: isMyDeptFlag,
        id: d.id, dept: d.dept,
        totalBudget: d.totalBudgetAmt,
        commonActual: d.summary.payCorpCommon,
        directActual: d.summary.payDrExp,
        strategyActual: d.summary.paySbuCmn,
        totalActual: d.summary.payCorpCommon + d.summary.payDrExp + d.summary.paySbuCmn
      })));

      setBudgetToActualDirect(processedData.map(d => ({
        isMyDept: isMyDeptFlag,
        id: d.id, dept: d.dept,
        directAmt: d.directAmt,
        monthlyData: d.sortedMonthly
      })));
      
      setBudgetToActualPublic(processedData.map(d => ({
        isMyDept: isMyDeptFlag,
        id: d.id, dept: d.dept,
        commonBudgetAmt: d.commonBudgetAmt,
        monthlyData: d.sortedMonthly
      })));

      setBudgetToActualSbd(processedData.map(d => ({
        isMyDept: isMyDeptFlag,
        id: d.id, dept: d.dept,
        strategyBudgetAmt: d.strategyBudgetAmt,
        monthlyData: d.sortedMonthly
      })));

    } catch (e) {
      console.error('데이터 로드 실패:', e);
      enqueueSnackbar('데이터를 불러오는 중 오류가 발생했습니다.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
    
  }, [enqueueSnackbar, searchYear, startMonth, endMonth, user]); // 의존성 배열에 user 가드 유실 보완


  const handleSearch = () => {
    fetchInitialData(searchYear, startMonth, endMonth);
  };

  useEffect(() => {
    if (searchYear) {
      fetchInitialData(searchYear, startMonth, endMonth);
    }
  }, []);
  return (
    <>
    <Box className={classes.container}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6" className={classes.headerTitle} sx={{ m: 0 }}>
          예산vs실적 현황
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
          <Box className={classes.searchArea} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ bgcolor: '#444', color: '#fff', px: 2, py: 0.8, fontWeight: 600, fontSize: '0.85rem', borderRadius: '4px' }}>
              기간
            </Box>
            <Select
              size="small"
              value={searchYear}
              onChange={(e) => setSearchYear(e.target.value)}
              sx={{ minWidth: 100, height: 32 }}
            >
              {yearOptions.map(year => (
                <MenuItem key={year} value={year}>{year}년</MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              value={startMonth}
              onChange={(e) => {
                const newStart = e.target.value;
                setStartMonth(newStart);
                // 💡 [역전 방지 가드]: 만약 기존에 선택된 종료월이 새로 선택한 시작월보다 작아지면 종료월을 시작월과 동일하게 맞춤
                if (parseInt(endMonth, 10) < parseInt(newStart, 10)) {
                  setEndMonth(newStart);
                }
              }}
              sx={{ minWidth: 80, height: 32 }}
            >
              {monthOptions.map(m => (
                <MenuItem key={m} value={m}>{m}월</MenuItem>
              ))}
            </Select>
            
            <Typography sx={{ fontWeight: 600 }}>~</Typography>
            
            <Select 
              size="small" 
              value={endMonth} 
              onChange={(e) => setEndMonth(e.target.value)} 
              sx={{ minWidth: 80, height: 32 }}
            >
              {/* 🛠️ [차감 교정]: 전체 monthOptions 대신 시작 월(startMonth) 이상인 월만 필터링해서 노출 */}
              {monthOptions
                .filter(m => parseInt(m, 10) >= parseInt(startMonth, 10))
                .map(m => (
                  <MenuItem key={m} value={m}>{m}월</MenuItem>
                ))
              }
            </Select>


            <Button 
              variant="contained" 
              size="small" 
              onClick={handleSearch} // 클릭 이벤트 추가
              sx={{ bgcolor: '#0052cc', height: 32 }}
            >
              조회
            </Button>
          </Box>

          <Typography sx={{ fontSize: '0.75rem', color: '#666', fontWeight: 500 }}>
            (단위: 원)
          </Typography>
        </Box>
      </Box>
    </Box>
      <BudgetToActualComponents deptList={budgetToActual} searchYear={appliedRange.year} startMonth={appliedRange.start} endMonth={appliedRange.end} />
      <h4
        onClick={() => setDirectIsOpen(!isDirectOpen)}
        style={{ cursor: 'pointer', userSelect: 'none' }}
      >
        <span style={{ marginRight: '8px' }}>
          {isDirectOpen ? '▼' : '▶'}
        </span>
        <span style={{
          color: '#007bff',
          textDecoration: 'underline',
          textUnderlineOffset: '4px'
        }}>
          부서별 직접비 예산 계획 vs. 실적
        </span>
      </h4>
      {isDirectOpen && <BudgetToActualDirectComponents deptList={budgetToActualDirect} searchYear={appliedRange.year} startMonth={appliedRange.start} endMonth={appliedRange.end}/>}
      <h4
        onClick={() => setPublicIsOpen(!isPublicOpen)}
        style={{ cursor: 'pointer', userSelect: 'none' }}
      >
        <span style={{ marginRight: '8px' }}>
          {isPublicOpen ? '▼' : '▶'}
        </span>
        <span style={{
          color: '#007bff',
          textDecoration: 'underline',
          textUnderlineOffset: '4px'
        }}>
          부서별 공통비 예산 계획 vs. 실적
        </span>
      </h4>
      {isPublicOpen && <BudgetToActualPublicComponents deptList={budgetToActualPublic} searchYear={appliedRange.year} startMonth={appliedRange.start} endMonth={appliedRange.end}/>}
      <h4
        onClick={() => setSbdIsOpen(!isSbdOpen)}
        style={{ cursor: 'pointer', userSelect: 'none' }}
      >
        <span style={{ marginRight: '8px' }}>
          {isSbdOpen ? '▼' : '▶'}
        </span>
        <span style={{
          color: '#007bff',
          textDecoration: 'underline',
          textUnderlineOffset: '4px'
        }}>
          부서별 전략사업본부 공통비 예산 계획 vs. 실적
        </span>
      </h4>
      {isSbdOpen && <BudgetToActualSbdComponents deptList={budgetToActualSbd} searchYear={appliedRange.year} startMonth={appliedRange.start} endMonth={appliedRange.end} />}
    </>
  );
};

export default BudgetToActualContainer;
